import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Sparkles, Loader2, Coins, RefreshCw, ChevronRight, Heart, Compass, Lightbulb, Wine, PenLine, BarChart3, LockKeyhole } from "lucide-react";
import DemoLoop from "@/components/DemoLoop";
import AiBadge from "@/components/AiBadge";

const PREVIEW_PROFILE = {
  sweetness_pref: 22,
  body_pref: 64,
  tannin_pref: 51,
  acidity_pref: 73,
  complexity_pref: 68,
  preferred_styles: ["Blancs minéraux", "Rouges élégants", "Effervescents fins"],
  preferred_regions: ["Bourgogne", "Loire", "Champagne"],
  grapes_loved: ["Chardonnay", "Pinot Noir", "Chenin"],
  grapes_to_try: ["Trousseau", "Mondeuse", "Savagnin"],
};

function Slider({ label, value, left, right, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="text-[#8B95A1]">{left}</span>
        <span className="font-bold text-[#1F3A5F]">{label}</span>
        <span className="text-[#8B95A1]">{right}</span>
      </div>
      <div className="h-3 rounded-full bg-[#FCE4ED] overflow-hidden relative">
        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#E5276A] to-[#1F3A5F]" style={{ width: `${Math.max(2, Math.min(98, value || 0))}%` }} />
        <div className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 border-[#1F3A5F] shadow" style={{ left: `calc(${Math.max(2, Math.min(98, value || 0))}% - 8px)` }} />
      </div>
    </div>
  );
}

function WineCard({ w, tone = "pink", testId, pairingLabel }) {
  const border = tone === "navy" ? "border-[#1F3A5F]" : "border-[#E5276A]";
  return (
    <div data-testid={testId} className={`rounded-2xl border-l-4 ${border} bg-white border-y border-r border-[#1F3A5F]/10 p-5`}>
      <div className="flex items-start gap-2">
        <Wine className={`w-5 h-5 ${tone === "navy" ? "text-[#1F3A5F]" : "text-[#E5276A]"} shrink-0 mt-0.5`} />
        <div className="min-w-0">
          <h3 className="font-bold text-[#1F3A5F] text-base leading-tight">{w.name}</h3>
          <p className="text-xs text-[#8B95A1] mt-0.5">{w.producer} · {w.appellation}</p>
        </div>
        {w.price_range_eur && <span className="ml-auto text-[10px] font-bold text-[#1F3A5F] bg-[#FBF7F3] px-2 py-0.5 rounded-full shrink-0">{w.price_range_eur}</span>}
      </div>
      {w.cepages?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">{w.cepages.slice(0, 3).map((c, i) => (
          <span key={i} className="text-[10px] font-bold text-[#E5276A] bg-[#FCE4ED] px-1.5 py-0.5 rounded-full">{c}</span>
        ))}</div>
      )}
      <p className="text-sm text-[#4A5568] mt-3 leading-relaxed italic">« {w.reason} »</p>
      {w.pairing_idea && <p className="text-xs text-[#1F3A5F] mt-2"><b>{pairingLabel}</b> {w.pairing_idea}</p>}
    </div>
  );
}

export default function TasteProfile() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const [busy, setBusy] = useState(true);
  const [profile, setProfile] = useState(null);
  const [sugMood, setSugMood] = useState("");
  const [sugBudget, setSugBudget] = useState("");
  const [sugBusy, setSugBusy] = useState(false);
  const [sugOut, setSugOut] = useState(null);

  useEffect(() => { if (user) load(false); else setBusy(false); }, [user]); // eslint-disable-line

  const load = async (refresh) => {
    setBusy(true);
    try {
      const { data } = await api.post("/tlp/taste/profile", { refresh, lang });
      setProfile(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("tasteProfile.errorGeneric"));
    } finally { setBusy(false); }
  };

  const runSuggestions = async () => {
    setSugBusy(true);
    try {
      const { data } = await api.post("/tlp/taste/suggestions", {
        mood: sugMood || undefined,
        budget_eur: sugBudget ? parseInt(sugBudget) : undefined,
        lang,
      });
      setSugOut(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error === "insufficient_credits"
        ? t("tasteProfile.insufficientCredits")
        : e?.response?.data?.detail || t("tasteProfile.errorGeneric"));
    } finally { setSugBusy(false); }
  };

  const DEMO_STEPS = [
    { title: t("tasteProfile.demo.step1.title"), body: t("tasteProfile.demo.step1.body"), icon: PenLine, color: "text-[#1F3A5F]" },
    { title: t("tasteProfile.demo.step2.title"), body: t("tasteProfile.demo.step2.body"), icon: BarChart3, color: "text-[#E5276A]" },
    { title: t("tasteProfile.demo.step3.title"), body: t("tasteProfile.demo.step3.body"), icon: Heart, color: "text-[#E5276A]" },
    { title: t("tasteProfile.demo.step4.title"), body: t("tasteProfile.demo.step4.body"), icon: Sparkles, color: "text-[#1F3A5F]" },
  ];

  if (!user) {
    const p = PREVIEW_PROFILE;
    return (
      <div className="max-w-3xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="taste-profile-preview">
        <span className="chip-pink inline-flex items-center gap-1.5"><Heart className="w-3 h-3" /> {t("tasteProfile.chip")} <AiBadge variant="icon" /></span>
        <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">{t("tasteProfile.title")}</h1>
        <p className="text-[#4A5568] mt-3 max-w-2xl">{t("tasteProfile.description")}</p>

        {/* Demo animée : 4 étapes en boucle */}
        <div className="mt-7"><DemoLoop steps={DEMO_STEPS} testId="profile-preview-demo" /></div>

        {/* Aperçu visuel du profil (exemple) */}
        <div className="mt-8 relative" data-testid="profile-preview-card">
          <div className="absolute -top-3 left-5 z-10 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1F3A5F] text-white text-[10px] font-bold uppercase tracking-widest">
            <Sparkles className="w-3 h-3 text-[#E5276A]" /> {t("tasteProfile.example")}
          </div>
          <div className="app-card p-6 space-y-5 border-2 border-dashed border-[#E5276A]/30">
            <Slider testId="profile-axis-sucre" label={t("tasteProfile.axes.sweetness")} value={p.sweetness_pref} left={t("tasteProfile.axes.dry")} right={t("tasteProfile.axes.sweet")} />
            <Slider testId="profile-axis-corps" label={t("tasteProfile.axes.body")} value={p.body_pref} left={t("tasteProfile.axes.light")} right={t("tasteProfile.axes.full")} />
            <Slider testId="profile-axis-tanin" label={t("tasteProfile.axes.tannin")} value={p.tannin_pref} left={t("tasteProfile.axes.soft")} right={t("tasteProfile.axes.tannic")} />
            <Slider testId="profile-axis-acidite" label={t("tasteProfile.axes.acidity")} value={p.acidity_pref} left={t("tasteProfile.axes.round")} right={t("tasteProfile.axes.crisp")} />
            <Slider testId="profile-axis-complexite" label={t("tasteProfile.axes.complexity")} value={p.complexity_pref} left={t("tasteProfile.axes.simple")} right={t("tasteProfile.axes.complex")} />

            <div className="rounded-2xl bg-[#FBF7F3] border-l-4 border-[#E5276A] p-4 mt-2">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-1 inline-flex items-center gap-1.5">{t("tasteProfile.audreyReads")} <AiBadge variant="icon" /></div>
              <p className="text-[#1F3A5F] italic text-sm">{t("tasteProfile.previewAudreyNote")}</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 pt-2">
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">{t("tasteProfile.stylesPref")}</div>
                <div className="flex flex-wrap gap-2">{p.preferred_styles.map((s) => (
                  <span key={s} className="px-3 py-1 rounded-full bg-white border border-[#1F3A5F]/12 text-xs text-[#1F3A5F]">{s}</span>
                ))}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">{t("tasteProfile.grapesLoved")}</div>
                <div className="flex flex-wrap gap-2">{p.grapes_loved.map((s) => (
                  <span key={s} className="px-3 py-1 rounded-full bg-[#1F3A5F] text-white text-xs">{s}</span>
                ))}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">{t("tasteProfile.regionsFav")}</div>
                <div className="flex flex-wrap gap-2">{p.preferred_regions.map((s) => (
                  <span key={s} className="px-3 py-1 rounded-full bg-white border border-[#1F3A5F]/12 text-xs text-[#1F3A5F]">{s}</span>
                ))}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">{t("tasteProfile.toDiscover")}</div>
                <div className="flex flex-wrap gap-2">{p.grapes_to_try.map((s) => (
                  <span key={s} className="px-3 py-1 rounded-full bg-[#FCE4ED] text-[#E5276A] text-xs font-bold">{s}</span>
                ))}</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA — création de compte */}
        <div className="mt-10 rounded-3xl border-2 border-[#E5276A] bg-gradient-to-br from-[#FCE4ED]/60 via-white to-[#FBF7F3] p-6 md:p-8 relative overflow-hidden">
          <LockKeyhole className="absolute -top-6 -right-6 w-32 h-32 text-[#E5276A]/8" />
          <div className="relative">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#E5276A] text-white text-[10px] font-bold uppercase tracking-widest">
              <Sparkles className="w-3 h-3" /> {t("tasteProfile.freeNoCard")}
            </span>
            <h2 className="font-display text-2xl md:text-3xl text-[#1F3A5F] mt-3 leading-tight">
              {t("tasteProfile.discoverYour")} <span className="text-[#E5276A]">{t("tasteProfile.yourSignature")}</span>
            </h2>
            <p className="text-[#4A5568] mt-2 max-w-xl text-sm md:text-base">
              {t("tasteProfile.needMoreNotes")}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Link to="/register?next=/taste/profile" data-testid="profile-preview-cta-register"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] transition-colors">
                <Sparkles className="w-4 h-4" /> {t("tasteProfile.registerFreeBtn")}
              </Link>
              <Link to="/login?next=/taste/profile" data-testid="profile-preview-cta-login"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-[#1F3A5F]/20 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A] hover:text-[#E5276A] transition-colors">
                {t("tasteProfile.alreadyAccount")}
              </Link>
            </div>
            <p className="text-xs text-[#8B95A1] mt-4 inline-flex items-center gap-1.5">
              <ChevronRight className="w-3 h-3 text-[#E5276A]" /> {t("tasteProfile.freeCreditNote")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="taste-profile">
      <span className="chip-pink inline-flex items-center gap-1.5"><Heart className="w-3 h-3" /> {t("tasteProfile.chip")}</span>
      <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">{t("tasteProfile.title")}</h1>
      <p className="text-[#4A5568] mt-3 max-w-2xl">{t("tasteProfile.audreyDescShort")}</p>

      {busy && <div className="mt-12 flex items-center gap-2 text-[#8B95A1]"><Loader2 className="w-5 h-5 animate-spin" /> {t("tasteProfile.analyzing")}</div>}

      {!busy && profile?.empty && (
        <div className="mt-10 app-card p-6 text-center" data-testid="profile-empty">
          <p className="text-sm text-[#4A5568]">{profile.message}</p>
          <Link to="/carnet" className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold">{t("tasteProfile.addTasting")} <ChevronRight className="w-4 h-4" /></Link>
        </div>
      )}

      {!busy && profile && !profile.empty && (
        <div className="mt-10 space-y-8" data-testid="profile-results">
          <div className="app-card p-6 space-y-5">
            <Slider testId="profile-axis-sucre" label={t("tasteProfile.axes.sweetness")} value={profile.sweetness_pref} left={t("tasteProfile.axes.dry")} right={t("tasteProfile.axes.sweet")} />
            <Slider testId="profile-axis-corps" label={t("tasteProfile.axes.body")} value={profile.body_pref} left={t("tasteProfile.axes.light")} right={t("tasteProfile.axes.full")} />
            <Slider testId="profile-axis-tanin" label={t("tasteProfile.axes.tannin")} value={profile.tannin_pref} left={t("tasteProfile.axes.soft")} right={t("tasteProfile.axes.tannic")} />
            <Slider testId="profile-axis-acidite" label={t("tasteProfile.axes.acidity")} value={profile.acidity_pref} left={t("tasteProfile.axes.round")} right={t("tasteProfile.axes.crisp")} />
            <Slider testId="profile-axis-complexite" label={t("tasteProfile.axes.complexity")} value={profile.complexity_pref} left={t("tasteProfile.axes.simple")} right={t("tasteProfile.axes.complex")} />
          </div>

          {profile.audrey_note && (
            <div className="rounded-2xl bg-[#FBF7F3] border-l-4 border-[#E5276A] p-5">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-1">{t("tasteProfile.audreyReads")}</div>
              <p className="text-[#1F3A5F] italic">{profile.audrey_note}</p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">{t("tasteProfile.stylesPref")}</div>
              <div className="flex flex-wrap gap-2">{(profile.preferred_styles || []).map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full bg-white border border-[#1F3A5F]/12 text-sm text-[#1F3A5F]">{s}</span>
              ))}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">{t("tasteProfile.regionsFav")}</div>
              <div className="flex flex-wrap gap-2">{(profile.preferred_regions || []).map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full bg-white border border-[#1F3A5F]/12 text-sm text-[#1F3A5F]">{s}</span>
              ))}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">{t("tasteProfile.grapesLovedLong")}</div>
              <div className="flex flex-wrap gap-2">{(profile.grapes_loved || []).map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full bg-[#1F3A5F] text-white text-sm">{s}</span>
              ))}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold mb-2">{t("tasteProfile.toDiscover")}</div>
              <div className="flex flex-wrap gap-2">{(profile.grapes_to_try || []).map((s) => (
                <span key={s} className="px-3 py-1.5 rounded-full bg-[#FCE4ED] text-[#E5276A] text-sm font-bold">{s}</span>
              ))}</div>
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2 pt-2 border-t border-[#1F3A5F]/8">
            <div className="text-xs text-[#8B95A1]">
              {t("tasteProfile.computedFrom").replace("{n}", profile.tasting_count)} · {profile.cached ? `(${t("tasteProfile.cached")})` : `(${t("tasteProfile.recomputed")})`}
            </div>
            <button onClick={() => load(true)} data-testid="profile-refresh"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F] hover:border-[#E5276A]">
              <RefreshCw className="w-4 h-4" /> {t("tasteProfile.refreshCredits")}
            </button>
          </div>

          {/* ━━━━━ SUGGESTIONS (embedded section) ━━━━━ */}
          <section className="pt-8 mt-4 border-t-2 border-[#FCE4ED]" data-testid="taste-suggestions">
            <span className="chip-pink inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> {t("tasteProfile.sug.chip")}</span>
            <h2 className="font-display text-3xl text-[#1F3A5F] mt-3">{t("tasteProfile.suggestionsTitle")}</h2>
            <p className="text-sm text-[#4A5568] mt-2 mb-5">{t("tasteProfile.suggestionsBody")}</p>

            <div className="app-card p-5 grid sm:grid-cols-2 gap-3 mb-6">
              <label className="block sm:col-span-2">
                <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">{t("tasteProfile.sug.moodLabel")}</span>
                <input value={sugMood} onChange={(e) => setSugMood(e.target.value)} data-testid="suggestions-mood"
                  placeholder={t("tasteProfile.sug.moodPlaceholder")}
                  className="mt-1 w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-4 py-2 text-sm text-[#1F3A5F]" />
              </label>
              <label className="block">
                <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">{t("tasteProfile.sug.budgetLabel")}</span>
                <input type="number" min="5" max="500" value={sugBudget} onChange={(e) => setSugBudget(e.target.value)} data-testid="suggestions-budget"
                  className="mt-1 w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] outline-none rounded-lg px-4 py-2 text-sm text-[#1F3A5F]" />
              </label>
              <button onClick={runSuggestions} disabled={sugBusy} data-testid="suggestions-run"
                className="self-end inline-flex items-center justify-center gap-2 px-5 py-2 rounded-xl bg-[#E5276A] text-white font-bold hover:bg-[#C01661] disabled:opacity-50">
                {sugBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {t("tasteProfile.sug.run")}
              </button>
            </div>

            {sugOut?.empty && (
              <div className="app-card p-5 text-center text-sm text-[#4A5568]" data-testid="suggestions-empty">{sugOut.message}</div>
            )}

            {sugOut && !sugOut.empty && (
              <div className="space-y-8" data-testid="suggestions-results">
                {sugOut.profile_summary && (
                  <div className="rounded-2xl bg-[#FBF7F3] border-l-4 border-[#E5276A] p-4">
                    <p className="text-[#1F3A5F] italic text-sm">{sugOut.profile_summary}</p>
                  </div>
                )}
                <div>
                  <h3 className="font-display text-xl text-[#1F3A5F] mb-3 inline-flex items-center gap-2"><Heart className="w-4 h-4 text-[#E5276A]" /> {t("tasteProfile.similar")}</h3>
                  <div className="grid md:grid-cols-3 gap-3">
                    {(sugOut.similar || []).map((w, i) => <WineCard key={i} w={w} tone="pink" testId={`suggestion-similar-${i}`} pairingLabel={t("tasteProfile.card.pairing")} />)}
                  </div>
                </div>
                <div>
                  <h3 className="font-display text-xl text-[#1F3A5F] mb-3 inline-flex items-center gap-2"><Compass className="w-4 h-4 text-[#1F3A5F]" /> {t("tasteProfile.broaden")}</h3>
                  <div className="grid md:grid-cols-3 gap-3">
                    {(sugOut.horizon || []).map((w, i) => <WineCard key={i} w={w} tone="navy" testId={`suggestion-horizon-${i}`} pairingLabel={t("tasteProfile.card.pairing")} />)}
                  </div>
                </div>
                {sugOut.audrey_pick && (
                  <div>
                    <h3 className="font-display text-xl text-[#1F3A5F] mb-3 inline-flex items-center gap-2"><Lightbulb className="w-4 h-4 text-amber-500" /> {t("tasteProfile.favourite")}</h3>
                    <WineCard w={sugOut.audrey_pick} tone="pink" testId="suggestion-audrey-pick" pairingLabel={t("tasteProfile.card.pairing")} />
                  </div>
                )}
                {sugOut.billing?.credits_remaining != null && (
                  <p className="text-xs text-[#8B95A1] text-center"><Coins className="w-3 h-3 inline" /> {t("tasteProfile.sug.creditsLeft").replace("{n}", sugOut.billing.credits_remaining)}</p>
                )}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
